import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createPointsReward,
  deactivatePointsReward,
  listPointsRewards,
  updatePointsReward,
  type CreatePointsRewardPayload,
  type UpdatePointsRewardPayload,
} from '../../../api/points';
import { useAuth } from '../../auth/AuthProvider';

function useRequireToken() {
  const { tokens } = useAuth();
  return () => {
    if (!tokens?.access_token) {
      throw new Error('Authenticated admin session is required.');
    }
    return tokens.access_token;
  };
}

export function pointsRewardsKey(includeInactive: boolean) {
  return ['points-rewards', includeInactive] as const;
}

export function usePointsRewards(includeInactive = false) {
  const getToken = useRequireToken();

  return useQuery({
    queryKey: pointsRewardsKey(includeInactive),
    queryFn: () => listPointsRewards(getToken(), includeInactive),
  });
}

function useInvalidateRewards() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['points-rewards'] });
}

export function useCreatePointsReward() {
  const getToken = useRequireToken();
  const invalidate = useInvalidateRewards();

  return useMutation({
    mutationFn: (payload: CreatePointsRewardPayload) => createPointsReward(payload, getToken()),
    onSuccess: invalidate,
  });
}

export function useUpdatePointsReward() {
  const getToken = useRequireToken();
  const invalidate = useInvalidateRewards();

  return useMutation({
    mutationFn: ({ id, payload }: { readonly id: string; readonly payload: UpdatePointsRewardPayload }) =>
      updatePointsReward(id, payload, getToken()),
    onSuccess: invalidate,
  });
}

export function useDeactivatePointsReward() {
  const getToken = useRequireToken();
  const invalidate = useInvalidateRewards();

  return useMutation({
    mutationFn: (id: string) => deactivatePointsReward(id, getToken()),
    onSuccess: invalidate,
  });
}
